/* eslint-disable no-nested-ternary */
import React, { useState, useEffect } from "react";

import { Tooltip } from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import ShipeeziNewGrid, {
  Column,
  GeneralActions,
  Icons as GridIcons,
} from "Components/AppGrid/shipeezi-new-grid";
import classnames from "classnames";
import { useTranslation } from "react-i18next";
import { FiEdit, FiEye, FiUserPlus } from "react-icons/fi";
import { MdOutlineMail, MdOutlineCancel } from "react-icons/md";
import { RiUserSettingsLine } from "react-icons/ri";
import { Link, useHistory } from "react-router-dom";
import { Badge } from "reactstrap";

import Button, { Color, Colors } from "../../../../../../components/Form/Button";
import ChangeStatusModal from "../../../../../../components/Generic/ConfirmChangeStatusModal";
import Modal, { ModalType } from "../../../../../../components/Generic/Modal";
import { NotificationToast } from "../../../../../../components/Generic/Toast/NotificationToast";
import RowActionRoutePlanner from "../../../../../../components/RowActionRoutePlanner";
import { PRIVATE_ROUTES, TYPE_OF_USER, TYPE_OF_USER_MOVING } from "../../../../../../constants";
import { useAppState } from "../../../../../../context/StateContext";
import useUserContext from "../../../../../../hooks/useUserContext";
import { DifferentLocation } from "../../../../../../models/location";
import { Project } from "../../../../../../models/project";
import { Row } from "../../../../../../models/shipeezi.table";
import { Roles, User } from "../../../../../../models/user";
import CompanyService from "../../../../../../services/company.service";
import { UserStatus, UserTypes } from "../../../../../../util/enums";
import { onSaveItemSelected } from "../../../../../../util/onSaveItemSelected";
import { isMoving } from "../../../../../../util/validation";
import useRoutePlannerActions from "./hooks/useRoutePlannerActions";
import { AssignedRoleToUserModal } from "./Modal/AssignedRoleToUserModal";
import { ModalToInviteUser } from "./ModalToInviteUser";
import useStyles from "./styles";

interface ResendInviteUserProps {
  id: number;
  companyId?: number;
}

const Users = () => {
  const classes = useStyles();
  const { t: translate } = useTranslation();
  const history = useHistory();
  const {
    rest: { users },
    table: {
      isFetching,
      total,
      page,
      pageSize,
      hasErrors,
      setHasErrors,
      setCurrentPage,
      setPageSize,
      handleSearch,
      handleFilter,
      handleSort,
    },
    refetchUserList,
    isUpdating,
    updateUser,
  } = useUserContext();

  const { useAddToRoutePlanner, useRemoveFromRoutePlanner } = useRoutePlannerActions();

  const {
    mutate: addToRoutePlanner,
    isLoading: addingToRoutePlanner,
    variables: userToAdd,
  } = useAddToRoutePlanner();

  const {
    mutate: removeFromRoutePlanner,
    isLoading: removingFromRoutePlanner,
    variables: userIdToRemove,
  } = useRemoveFromRoutePlanner();

  const [selectedUser, setSelectedUser] = useState<User>();
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [rows, setRows] = useState<Row[]>([]);
  const [openConfirmation, setOpenConfirmation] = useState<boolean>(false);
  const [openInviteUser, setOpenInviteUser] = useState<boolean>(false);
  const [clearSelection, setClearSelection] = useState<boolean>(false);

  const [userRevokeInvite, setUserRevokeInvite] = useState<User>();

  const { currentUser } = useAppState();

  const { mutate: inviteUser, isLoading: isLoadingInviteUser } = useMutation(
    ["company.invite.user"],
    ({
      userEmail,
      roleId,
      typeUser,
      projects,
    }: {
      userEmail: string;
      roleId: number;
      typeUser: string;
      projects?: Project[];
    }) => CompanyService.inviteUserInCompany(userEmail, undefined, roleId, typeUser, projects),
    {
      onSuccess: () => {
        setOpenInviteUser(false);
        NotificationToast.success(translate("inviteSentSuccessfully"));
        refetchUserList();
      },
    }
  );

  const { mutate: resendInviteUser, isLoading: isLoadingResendInviteUser } = useMutation(
    ["company.resend.invite.user"],
    ({ id, companyId }: ResendInviteUserProps) => CompanyService.userResendInvite(id, companyId),
    {
      onSuccess: () => {
        NotificationToast.success(translate("operationPerformedSuccessfully"));
        refetchUserList();
      },
    }
  );

  const { mutate: revokeInviteUser, isLoading: isLoadingRevokeInviteUser } = useMutation(
    ["company.resend.invite.user"],
    ({ id, companyId }: ResendInviteUserProps) => CompanyService.userRevokeInvite(id, companyId),
    {
      onSuccess: () => {
        NotificationToast.success(translate("operationPerformedSuccessfully"));
        setUserRevokeInvite(undefined);
        refetchUserList();
      },
    }
  );

  const badgeStatus = (status?: string) => {
    const getStyleStatus = () => {
      if (status === UserStatus.ACTIVE) {
        return classes.statusBadgeActive;
      }

      if (status === UserStatus.INVITATION_SENT) {
        return classes.statusBadgeInvitationSent;
      }

      if (status === UserStatus.REJECTED) {
        return classes.statusBadgeRejected;
      }

      return "";
    };

    return (
      <Badge
        className={`
                ${classes.statusBadge}
                ${getStyleStatus()}
            `}
      >
        {status}
      </Badge>
    );
  };

  const editUser = (userId: number) => {
    history.push(`${PRIVATE_ROUTES.USERS_PEOPLE_MNT}/${userId}`);
  };

  const changeStatusOfUser = (userId: number) => {
    const userToUpdate = users?.find((user) => Number(user.id) === Number(userId));

    if (userToUpdate && userToUpdate?.status) {
      updateUser({
        id: userId,
        user: {
          status:
            userToUpdate.userCompany?.status === UserStatus.INACTIVE
              ? UserStatus.ACTIVE
              : UserStatus.INACTIVE,
        } as User,
      });
    }
  };

  const handleEditButton = (userId: string | number, user: User) => {
    if (
      user?.userCompany?.status !== UserStatus.INVITATION_SENT &&
      user?.userCompany?.status !== UserStatus.REJECTED &&
      user?.userCompany?.status !== UserStatus.REVOKED
    ) {
      return (
        <Tooltip data-cy="action-edit" title={translate("edit")} placement="top">
          <div>
            <FiEdit
              onClick={() => editUser(Number(userId))}
              color="#475467"
              className={classes.button}
              size={18}
            />
          </div>
        </Tooltip>
      );
    }

    return null;
  };

  const viewUser = (userId: number, user: User) => {
    if (
      user?.userCompany?.status !== UserStatus.INVITATION_SENT &&
      user?.userCompany?.status !== UserStatus.REJECTED &&
      user?.userCompany?.status !== UserStatus.REVOKED
    ) {
      return (
        <Tooltip title={translate("view") || ""} placement="top">
          <Link
            data-cy="action-view"
            to={{
              pathname: `${PRIVATE_ROUTES.USERS_PEOPLE_MNT}/${userId}`,
              state: { view: "true" },
            }}
          >
            <FiEye size={18} color="#475467" className={classes.button} />
          </Link>
        </Tooltip>
      );
    }

    return null;
  };

  const assignRoleToUser = (userId: number, user: User) => {
    if (
      user?.userCompany?.status !== UserStatus.INVITATION_SENT &&
      user?.userCompany?.status !== UserStatus.REJECTED &&
      user?.userCompany?.status !== UserStatus.REVOKED
    ) {
      return (
        <Tooltip title={translate("assignRole") || ""} placement="top">
          <div>
            <RiUserSettingsLine
              data-cy="action-assign"
              size={18}
              color="#475467"
              className={classes.button}
              onClick={() => {
                const userToUpdate = users?.find((user) => Number(user.id) === userId);

                setSelectedUser(userToUpdate);
                setOpenModal(true);
              }}
            />
          </div>
        </Tooltip>
      );
    }

    return null;
  };

  const handleChangeStatusToUser = (userId: number, user: User) => {
    if (
      user?.userCompany?.status !== UserStatus.INVITATION_SENT &&
      user?.userCompany?.status !== UserStatus.REJECTED &&
      user?.userCompany?.status !== UserStatus.REVOKED
    ) {
      return (
        <Tooltip title={translate("changeStatus") || ""} placement="top">
          <div>
            <GridIcons.status
              data-cy="change-status"
              color="#475467"
              className={classes.button}
              onClick={() => changeStatusOfUser(userId)}
            />
          </div>
        </Tooltip>
      );
    }

    return null;
  };

  const handleSendEmailButton = (userId: number, user: User) => {
    if (
      [UserStatus.INVITATION_SENT, UserStatus.REJECTED, UserStatus.REVOKED].includes(
        user?.userCompany?.status as UserStatus
      )
    ) {
      return (
        <Tooltip title={translate("resendInvite") || ""} placement="top">
          <div
            data-cy="resendInviteUser"
            onClick={() =>
              resendInviteUser({ id: userId, companyId: user?.userCompany?.companyId })
            }
          >
            <MdOutlineMail size={18} color="#475467" className={classes.button} />
          </div>
        </Tooltip>
      );
    }

    return null;
  };

  const handleRevokeInviteButton = (userId: number, user: User) => {
    if (
      user?.userCompany?.status === UserStatus.INVITATION_SENT &&
      userId &&
      user?.userCompany?.companyId
    ) {
      return (
        <Tooltip title={translate("cancelInvite") || ""} placement="top">
          <div
            data-cy="revokeInviteUser"
            onClick={() => {
              setUserRevokeInvite({
                id: userId,
                companyId: Number(user?.userCompany?.companyId),
              } as User);
            }}
          >
            <MdOutlineCancel size={18} color="#F04438" className={classes.button} />
          </div>
        </Tooltip>
      );
    }

    return null;
  };

  const renderButtonToChangeStatus = () => {
    if (rows.length > 0) {
      return (
        <Button
          tooltip={translate("changeStatus")}
          className={classes.changeButtonStatus}
          customColor={Color.INFO}
          data-cy="changeStatus"
          onClick={() => setOpenConfirmation(true)}
        >
          <GridIcons.status color={Colors.INFO.text.color} size={16} className="mr-2" />
          {translate("changeStatus")}
        </Button>
      );
    }
  };

  const renderButtonToInviteUser = () => {
    return (
      <Button
        tooltip={translate("inviteUser")}
        className={classes.changeButtonStatus}
        customColor={Color.INFO}
        data-cy="inviteUser"
        onClick={() => setOpenInviteUser(true)}
      >
        <FiUserPlus size={16} className="mr-2" />
        {translate("inviteUser")}
      </Button>
    );
  };

  const removeRowsWithStatusDifferentActiveOrInactive = (usersIdSelected: number[]): number[] => {
    const newUsersIdSelected = [];

    for (const userIdSelected of usersIdSelected) {
      const user = users?.find((user) => user.id === userIdSelected);

      if (
        user &&
        (user?.userCompany?.status === UserStatus.ACTIVE ||
          user?.userCompany?.status === UserStatus.INACTIVE)
      ) {
        newUsersIdSelected.push(user.id);
      }
    }

    return newUsersIdSelected;
  };

  const handleSaveUsersSelected = (rowsArr: number[]) => {
    const response = onSaveItemSelected(
      rowsArr,
      rows,
      users,
      removeRowsWithStatusDifferentActiveOrInactive
    );

    setRows([...response]);
  };

  const handleAddToRoutePlanner = (user?: User) => {
    if (!user) return;

    addToRoutePlanner(user);
  };

  const handleRemoveFromRoutePlanner = (id?: number) => {
    if (!id) return;

    removeFromRoutePlanner(id);
  };

  const renderRoutePlannerAction = (id: number) => {
    if (!users) return;

    const userToUpdate = users.find((user) => Number(user.id) === Number(id));

    if (
      !userToUpdate?.emailConfirmed &&
      userToUpdate?.status !== UserStatus.INACTIVE &&
      userToUpdate?.typeUser !== UserTypes.DRIVER
    )
      return;

    return (
      <RowActionRoutePlanner
        uuid={userToUpdate?.externalUuid}
        loading={
          (userToAdd?.id === userToUpdate?.id && addingToRoutePlanner) ||
          (userIdToRemove === userToUpdate?.id && removingFromRoutePlanner)
        }
        add={() => handleAddToRoutePlanner(userToUpdate)}
        remove={() => handleRemoveFromRoutePlanner(userToUpdate?.id)}
      />
    );
  };

  const columns: Column[] = [
    {
      name: "code",
      title: translate("code"),
      formatComponent: (row: string, object: User) => object?.userCompany?.code,
    },
    {
      name: "typeUser",
      hideSearch: true,
      hideSort: true,
      title: translate("type"),
      formatComponent: (row: string, object: User) => object?.userCompany?.typeUser,
    },
    {
      name: "fullName",
      title: translate("name"),
      searchIdentifier: "full_name",
    },
    { name: "username", title: translate("username") },
    {
      name: "email",
      title: translate("email"),
    },
    {
      name: "role",
      title: translate("role"),
      hideSort: true,
      formatComponent: (role: Roles, user: User) => {
        return user && user.userCompany && user.userCompany.role ? user.userCompany.role?.name : "";
      },
      hideSearch: true,
    },
    {
      name: "location",
      title: translate("location"),
      hideSort: true,
      hideSearch: true,
      formatText: (location: DifferentLocation) =>
        location && location?.suburb
          ? location?.suburb
          : location?.country
          ? location?.country
          : location?.addressLine2,
    },
    {
      name: "status",
      title: translate("status"),
      hideSearch: true,
      formatComponent: (status: string, object: User) => badgeStatus(object?.userCompany?.status),
      hideSort: true,
    },
  ];

  const generalActions: GeneralActions = {
    orderBy: {
      left: ["handleSearch", "handleFilterBy"],
      right: ["handleChangeStatus", "handleNewRecord", "handleInviteUser"],
    },
    handleSearch: {
      onSearch: (filters) => {
        handleSearch(filters);
      },
    },
    handleFilterBy: {
      categories: [
        {
          name: translate("type"),
          column: "usersCompanies.typeUser",
          options: isMoving(currentUser?.company)
            ? TYPE_OF_USER_MOVING?.map((type) => type?.name)
            : TYPE_OF_USER.map((type) => type?.name),
        },
        {
          name: translate("status"),
          column: "usersCompanies.status",
          options: UserStatus && Object.values(UserStatus),
        },
      ],
      onChange: handleFilter,
    },
    handleChangeStatus: renderButtonToChangeStatus(),

    handleNewRecord: {
      text: translate("createNewUser"),
      action: () => history.push(PRIVATE_ROUTES.USERS_PEOPLE_MNT),
      tooltip: translate("createNewUser"),
    },

    handleInviteUser: currentUser?.company.profileId !== 4 && renderButtonToInviteUser(),
  };

  useEffect(() => {
    if (hasErrors !== undefined && !hasErrors) {
      NotificationToast.success(translate("operationPerformedSuccessfully"), {
        id: "request.success.default.user",
      });
      setHasErrors(undefined);
      setClearSelection(false);
      setRows([]);
    }
  }, [hasErrors]);

  return (
    <div data-cy="peopleDataList" className={classes.usersComponentWrapper}>
      <ShipeeziNewGrid
        data={users || []}
        columns={columns}
        keyExtractor={(data) => data.id}
        totalItems={total}
        loadingData={isFetching}
        emptyData={translate("noInformationToBeShown")}
        pageSize={pageSize}
        pageSizeList={[20, 40, 60, 100]}
        currentPage={page}
        onChangePageSize={setPageSize}
        onNextPage={setCurrentPage}
        onPreviousPage={setCurrentPage}
        onGoToPage={setCurrentPage}
        rowActions={{
          orderBy: [
            "resendInvite",
            "revokeInvite",
            "edit",
            "view",
            "assign",
            "changeStatus",
            "handleRoutePlanner",
          ],
        }}
        rowActionsExtra={[
          {
            title: translate("resendInvite"),
            key: "resendInvite",
            buttonAndAction: handleSendEmailButton,
          },
          {
            title: translate("cancelInvite"),
            key: "revokeInvite",
            buttonAndAction: handleRevokeInviteButton,
          },
          { title: translate("edit"), key: "edit", buttonAndAction: handleEditButton },
          { title: translate("view"), key: "view", buttonAndAction: viewUser },
          {
            title: translate("assignRole"),
            key: "assign",
            buttonAndAction: assignRoleToUser,
          },
          {
            title: translate("changeStatus"),
            key: "changeStatus",
            buttonAndAction: handleChangeStatusToUser,
          },
          ...(currentUser?.company?.useRoutePlanner
            ? [
                {
                  title: translate("routePlanner"),
                  key: "handleRoutePlanner",
                  buttonAndAction: renderRoutePlannerAction,
                },
              ]
            : []),
        ]}
        clearSelection={clearSelection}
        handleSelect={(rowsArr: number[]) => handleSaveUsersSelected(rowsArr)}
        positionActionsColumn="right"
        onSorting={handleSort}
        generalActions={generalActions}
      />

      <Modal
        className={classes.revokeModal}
        open={!!userRevokeInvite}
        type={ModalType.INFO}
        title={translate("revokeInvitation")}
        showActionButtons
        handleClose={(_, reason) => {
          if (reason !== "backdropClick") setUserRevokeInvite(undefined);
        }}
        actionButtonProps={{
          textSave: translate("yesRevoke"),
          customColorSave: Color.SECONDARY,
          customColorCancel: Color.DARK,
          loading: isLoadingRevokeInviteUser,
          onCancel: () => setUserRevokeInvite(undefined),
          onSave: () => {
            if (userRevokeInvite)
              revokeInviteUser({
                id: userRevokeInvite?.id,
                companyId: userRevokeInvite?.companyId,
              });
          },
        }}
      >
        <p className={classnames(classes.descriptionRevokeModal, classes.textModal)}>
          {translate("doYouWantToRevokeTheInvitationSentTheUserWillNoLonge")}.
        </p>
        <span className={classnames(classes.subDescriptionRevokeModal, classes.textModal)}>
          {translate("youCanAlwaysInviteThemAgain")}
        </span>
      </Modal>

      {openModal && (
        <AssignedRoleToUserModal
          isOpen={openModal}
          closeModal={() => setOpenModal(false)}
          selectedUser={selectedUser}
          isLoadingUpdate={isUpdating}
          updateUser={(roleId: number | null, userId: number, user: User) =>
            updateUser({
              id: userId,
              user: { roleId: roleId ?? user.roleId } as User,
            })
          }
        />
      )}

      {openConfirmation && (
        <ChangeStatusModal
          open={openConfirmation}
          rows={rows}
          isLoading={isUpdating}
          hasErrors={hasErrors}
          closeModal={() => setOpenConfirmation(false)}
          update={(rowId, status) => {
            if (rowId && status) {
              updateUser({
                id: rowId,
                user: { status } as User,
              });
              setClearSelection(true);
            }
          }}
        />
      )}

      {openInviteUser && (
        <ModalToInviteUser
          isOpen={openInviteUser}
          isLoading={isLoadingInviteUser || isLoadingResendInviteUser}
          onClose={() => setOpenInviteUser(false)}
          onInviteUser={(email, roleId, typeUser, projects) =>
            inviteUser({ userEmail: email, roleId, typeUser, projects })
          }
        />
      )}
    </div>
  );
};

export default Users;
